import React, { useState } from "react";
import { CButton, CBadge, CCollapse } from "@coreui/react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { SectionCard } from "../__sections";
import { colors } from "../../../config/theme";
import PlusIcon from "../../../components/icons/PlusIcon";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import ScheduleEditor from "../__sections/scheduleEditor";
import { emptySchedule } from "../../../helpers/types";
import { formatDateRange } from "../../../helpers/general";
import ChevronIcon from "../../../components/icons/ChevroIcon";

export default function Schedules() {
  const { control, watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "schedules",
  });

  const [openIndex, setOpenIndex] = useState<number | null>(
    fields.length === 1 ? 0 : null,
  );

  const toggle = (index: number) =>
    setOpenIndex((prev) => (prev === index ? null : index));

  const handleAdd = () => {
    append(emptySchedule());
    setOpenIndex(fields.length); // open the newly added one
  };

  const handleRemove = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    remove(index);
    if (openIndex === index) setOpenIndex(null);
    else if (openIndex !== null && openIndex > index)
      setOpenIndex(openIndex - 1);
  };

  return (
    <div className="animate-fade-in">
      <SectionCard
        title="Disponibilités & tarification"
        subtitle="Définissez un ou plusieurs calendriers (saisons, horaires, tarifs). Ces calendriers sont les valeurs par défaut de l'activité ; une option pourra les remplacer plus tard."
      >
        {fields.length === 0 && (
          <div
            className="text-center py-5 mb-3 d-flex flex-column align-items-center gap-2"
            style={{
              border: "1px dashed #ddd",
              borderRadius: "12px",
              color: "#999",
            }}
          >
            <div style={{ fontSize: "1.75rem" }}>📅</div>
            <div className="fw-semibold" style={{ color: "#777" }}>
              Aucun calendrier pour le moment
            </div>
            <div className="small" style={{ maxWidth: 360 }}>
              Ajoutez un calendrier pour définir quand cette activité est
              disponible et à quel prix.
            </div>
          </div>
        )}

        <div className="d-flex flex-column gap-2 mb-3">
          {fields.map((field, index) => {
            const isOpen = openIndex === index;
            const name = watch(`schedules.${index}.name`);
            const startDate = watch(`schedules.${index}.startDate`);
            const endDate = watch(`schedules.${index}.endDate`);
            const isActive = watch(`schedules.${index}.isActive`);

            return (
              <div
                key={field.id}
                style={{
                  border: isOpen
                    ? `1px solid ${colors.yellow}`
                    : "1px solid #ececec",
                  borderRadius: "12px",
                  overflow: "hidden",
                  transition: "border-color 0.15s ease",
                  boxShadow: isOpen ? "0 2px 10px rgba(0,0,0,0.05)" : "none",
                }}
              >
                <div
                  onClick={() => toggle(index)}
                  className="d-flex justify-content-between align-items-center px-3 py-3"
                  style={{
                    cursor: "pointer",
                    backgroundColor: isOpen ? "#fffbf0" : "#fafafa",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-3 flex-grow-1"
                    style={{ minWidth: 0 }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        backgroundColor: isOpen ? colors.yellow : "#eee",
                        color: isOpen ? "#fff" : "#888",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                      }}
                    >
                      {index + 1}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="fw-semibold text-truncate">
                        {name || `Calendrier #${index + 1}`}
                      </div>
                      <div className="small text-muted text-truncate">
                        {formatDateRange(startDate, endDate)}
                      </div>
                    </div>
                    {isActive === false && (
                      <CBadge
                        shape="rounded-pill"
                        style={{
                          backgroundColor: "#eee",
                          color: "#888",
                          fontWeight: 500,
                        }}
                      >
                        Inactif
                      </CBadge>
                    )}
                  </div>

                  <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    <CButton
                      size="sm"
                      variant="ghost"
                      color="danger"
                      onClick={(e) => handleRemove(e, index)}
                      title="Retirer ce calendrier"
                    >
                      <DeleteIcon width="14" fill="#dc3545" />
                    </CButton>
                    <div style={{ color: "#999" }}>
                      <ChevronIcon open={isOpen} />
                    </div>
                  </div>
                </div>

                <CCollapse visible={isOpen}>
                  <div
                    className="p-3 pt-4"
                    style={{ borderTop: "1px solid #ececec" }}
                  >
                    <ScheduleEditor namePrefix={`schedules.${index}`} />
                  </div>
                </CCollapse>
              </div>
            );
          })}
        </div>

        <CButton
          style={{
            backgroundColor: colors.yellow,
            border: "none",
            color: "#fff",
          }}
          size="sm"
          onClick={handleAdd}
        >
          <PlusIcon width="16" fill="#fff" /> Ajouter un calendrier
        </CButton>
      </SectionCard>
    </div>
  );
}
